/**
 * subscriptionService.ts
 *
 * Phase 5 — React Web subscription service.
 *
 * Responsibilities:
 *  - Initiate Paddle Checkout (subscriptions + consumable packs) via the
 *    `createPaddleCheckoutSession` Cloud Function.
 *  - Open the Paddle Customer Portal via the `createPaddlePortalSession`
 *    Cloud Function.
 *  - Real-time listeners for `subscriptions/{userId}` and
 *    `feature_credits/{userId}` Firestore documents.
 *  - Paginated read of `payment_transactions` for billing history.
 */

import {
    collection,
    doc,
    onSnapshot,
    orderBy,
    query,
    limit as firestoreLimit,
    startAfter,
    getDocs,
    where,
    DocumentSnapshot,
    Unsubscribe,
    QueryDocumentSnapshot,
    QueryConstraint,
    Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import {
    SubscriptionInfo,
    FeatureCredits,
    PaymentTransaction,
    parseTier,
    parseSubscriptionStatus,
    parseSubscriptionSource,
} from '../models/index';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Convert a Firestore Timestamp, ISO string, or undefined to a JS Date. */
function toDate(value: unknown): Date | undefined {
    if (!value) return undefined;
    if (value instanceof Timestamp) return value.toDate();
    if (typeof value === 'string') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? undefined : d;
    }
    if (typeof value === 'object' && 'toDate' in (value as object)) {
        return (value as { toDate: () => Date }).toDate();
    }
    return undefined;
}

// ── Parsers ───────────────────────────────────────────────────────────────────

function parseSubscriptionInfo(
    id: string,
    data: Record<string, unknown>
): SubscriptionInfo {
    return {
        userId: id,
        revenuecatEntitlementId: data.revenuecatEntitlementId as string | undefined,
        paddleCustomerId: data.paddleCustomerId as string | undefined,
        paddleSubscriptionId: data.paddleSubscriptionId as string | undefined,
        paddlePriceId: data.paddlePriceId as string | undefined,
        appleOriginalTransactionId: data.appleOriginalTransactionId as string | undefined,
        googlePurchaseToken: data.googlePurchaseToken as string | undefined,
        status: parseSubscriptionStatus(data.status as string),
        tier: parseTier(data.tier as string),
        source: parseSubscriptionSource(data.source as string),
        currentPeriodStart: toDate(data.currentPeriodStart),
        currentPeriodEnd: toDate(data.currentPeriodEnd),
        cancelAtPeriodEnd: (data.cancelAtPeriodEnd as boolean) ?? false,
        pendingDowngradeTier: data.pendingDowngradeTier
            ? parseTier(data.pendingDowngradeTier as string)
            : undefined,
        trialEnd: toDate(data.trialEnd),
        introOfferEligible: (data.introOfferEligible as boolean) ?? false,
        lastSyncedAt: toDate(data.lastSyncedAt),
    };
}

function parseFeatureCredits(
    id: string,
    data: Record<string, unknown>
): FeatureCredits {
    return {
        userId: id,
        extraTrips: (data.extraTrips as number) ?? 0,
        extraAiTokens: (data.extraAiTokens as number) ?? 0,
        extraStorageMb: (data.extraStorageMb as number) ?? 0,
        lastUpdatedAt: toDate(data.lastUpdatedAt),
    };
}

function parsePaymentTransaction(
    docSnap: QueryDocumentSnapshot
): PaymentTransaction {
    const data = docSnap.data() as Record<string, unknown>;
    return {
        id: docSnap.id,
        userId: data.userId as string,
        source: parseSubscriptionSource(data.source as string),
        paddleTransactionId: data.paddleTransactionId as string | undefined,
        appleTransactionId: data.appleTransactionId as string | undefined,
        googleOrderId: data.googleOrderId as string | undefined,
        amount: (data.amount as number) ?? 0,
        currency: (data.currency as string) ?? 'USD',
        status: (data.status as 'paid' | 'open' | 'void' | 'uncollectible') ?? 'open',
        tier: data.tier ? parseTier(data.tier as string) : undefined,
        productType: (data.productType as 'subscription' | 'consumable') ?? 'subscription',
        description: data.description as string | undefined,
        invoiceUrl: data.invoiceUrl as string | undefined,
        greenInvoiceUrl: data.greenInvoiceUrl as string | undefined,
        createdAt: toDate(data.createdAt) ?? new Date(),
        paidAt: toDate(data.paidAt),
    };
}

// ── Cloud Function callables ───────────────────────────────────────────────────

interface CreateCheckoutParams {
    userId: string;
    priceId: string;
    productType: 'subscription' | 'consumable';
}

interface CreateCheckoutResult {
    checkoutUrl?: string;
    transactionId?: string;
}

interface CreatePortalResult {
    portalUrl: string;
}

interface SyncRevenueCatParams {
    userId: string;
}

interface SyncRevenueCatResult {
    synced: boolean;
}

const _callCreateCheckout = httpsCallable<CreateCheckoutParams, CreateCheckoutResult>(
    functions,
    'createPaddleCheckoutSession'
);

const _callCreatePortal = httpsCallable<Record<string, never>, CreatePortalResult>(
    functions,
    'createPaddlePortalSession'
);

const _callSyncRevenueCat = httpsCallable<SyncRevenueCatParams, SyncRevenueCatResult>(
    functions,
    'syncRevenueCatStatus'
);

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Calls `createPaddleCheckoutSession` and returns a Paddle-hosted checkout URL
 * (or transactionId for use with Paddle.js Overlay Checkout).
 *
 * Works for both subscription price IDs and one-time consumable price IDs.
 */
export async function getCheckoutUrl(
    userId: string,
    priceId: string,
    productType: 'subscription' | 'consumable' = 'subscription'
): Promise<CreateCheckoutResult> {
    const result = await _callCreateCheckout({ userId, priceId, productType });
    return result.data;
}

/**
 * Calls `createPaddlePortalSession` and returns the Paddle Customer Portal URL.
 * Used on web only for: cancel, change plan, update payment method, download invoices.
 */
export async function getPortalUrl(): Promise<string> {
    const result = await _callCreatePortal({});
    return result.data.portalUrl;
}

/**
 * Triggers a mandatory RevenueCat status sync for the given user.
 * Always call this after sign-in (no rate-limit on login path).
 */
export async function syncRevenueCatStatus(userId: string): Promise<void> {
    await _callSyncRevenueCat({ userId });
}

/**
 * Attaches a real-time Firestore listener to `subscriptions/{userId}`.
 *
 * @param userId  Firebase UID.
 * @param onData  Called whenever the document changes; `null` if not found.
 * @param onError Optional error handler.
 * @returns Unsubscribe function.
 */
export function watchSubscription(
    userId: string,
    onData: (info: SubscriptionInfo | null) => void,
    onError?: (err: Error) => void
): Unsubscribe {
    const ref = doc(db, 'subscriptions', userId);
    return onSnapshot(
        ref,
        (snapshot) => {
            if (snapshot.exists()) {
                onData(
                    parseSubscriptionInfo(
                        userId,
                        snapshot.data() as Record<string, unknown>
                    )
                );
            } else {
                onData(null);
            }
        },
        (err) => {
            console.error('[subscriptionService] watchSubscription error:', err);
            onError?.(err);
        }
    );
}

/**
 * Attaches a real-time Firestore listener to `feature_credits/{userId}`.
 *
 * @returns Unsubscribe function.
 */
export function watchFeatureCredits(
    userId: string,
    onData: (credits: FeatureCredits | null) => void,
    onError?: (err: Error) => void
): Unsubscribe {
    const ref = doc(db, 'feature_credits', userId);
    return onSnapshot(
        ref,
        (snapshot) => {
            if (snapshot.exists()) {
                onData(
                    parseFeatureCredits(
                        userId,
                        snapshot.data() as Record<string, unknown>
                    )
                );
            } else {
                onData(null);
            }
        },
        (err) => {
            console.error('[subscriptionService] watchFeatureCredits error:', err);
            onError?.(err);
        }
    );
}

/** Sentinel value for pagination — pass to `getPaymentHistory` to begin from the start. */
export const PAYMENT_HISTORY_START = null;

/**
 * Fetches a page of payment transactions for the given user, ordered by
 * `createdAt` descending (most recent first).
 *
 * @param userId          Firebase UID.
 * @param pageLimit       Maximum documents to return (default 20).
 * @param afterDoc        Last document from previous page for cursor-based
 *                        pagination; pass `null` for the first page.
 * @returns `{ transactions, lastDoc }` — `lastDoc` is `null` when no more pages.
 */
export async function getPaymentHistory(
    userId: string,
    pageLimit = 20,
    afterDoc: DocumentSnapshot | null = null
): Promise<{
    transactions: PaymentTransaction[];
    lastDoc: DocumentSnapshot | null;
}> {
    const colRef = collection(db, 'payment_transactions');

    const constraints: QueryConstraint[] = [
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageLimit),
    ];

    if (afterDoc) {
        constraints.push(startAfter(afterDoc));
    }

    const q = query(colRef, ...constraints);

    const snapshot = await getDocs(q);
    const transactions = snapshot.docs.map(parsePaymentTransaction);

    const lastDoc =
        snapshot.docs.length === pageLimit
            ? snapshot.docs[snapshot.docs.length - 1]
            : null;

    return { transactions, lastDoc };
}
